import FormattedMessage from 'I18n/components/FormattedMessage';
import { usePortalCurrencies } from 'commerce-billing-lib/public/hooks';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import { useSendCrmMessageTopic } from 'crm-message-bus/useSendCrmMessageTopic';
import { COMPANY_TYPE_ID, CONTACT_TYPE_ID, DEAL_TYPE_ID, LINE_ITEM_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { Button } from 'foundations-components/transitional/components/Button';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { gate } from 'hub-http/gates';
import { useCallback, useState } from 'react';
import { ConvertToContractTooltip } from './ConvertToContractTooltip';
import { useContractPermissions } from '../../hooks/useContractPermissions';
import { useConversionCandidate } from './hooks/useFetchConversionCandidate';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function CardActions({
  fromObjectTypeId,
  fromObjectId,
  toObjectTypeId,
  canEditAssociation,
  associationTypeData,
  trackEvent = () => {},
  isUngated
}) {
  const [open, setOpen] = useState(false);
  const openObjectBuilder = useSendCrmMessageTopic(TOPIC_NAMES.OPEN_OBJECT_BUILDER);
  const {
    data
  } = useConversionCandidate(fromObjectTypeId, fromObjectId);
  const permissions = useContractPermissions();
  const {
    defaultCurrency,
    loading: defaultCurrencyLoading
  } = usePortalCurrencies();
  const hasPrivateBetaGate = isUngated(gate('Commerce:Contracts:PrivateBeta'));
  const openAssociationPanel = useCallback((builderProps = {}) => {
    return openObjectBuilder({
      objectTypeId: toObjectTypeId,
      association: {
        objectTypeId: fromObjectTypeId,
        objectId: fromObjectId,
        associationTypeId: associationTypeData.associationTypeId,
        associationCategory: associationTypeData.associationCategory
      },
      builderProps
    });
  }, [openObjectBuilder, toObjectTypeId, fromObjectTypeId, fromObjectId, associationTypeData.associationTypeId, associationTypeData.associationCategory]);
  const openConversionPanel = useCallback(() => {
    if (!permissions.canEditAssociation(canEditAssociation)) {
      return;
    }
    trackEvent('Click "Convert to Contract"');
    return openAssociationPanel({
      defaultProperties: {
        hs_name: data === null || data === void 0 ? void 0 : data.title,
        hs_currency_code: (data === null || data === void 0 ? void 0 : data.currencyCode) || defaultCurrency
      },
      additionalDefaultAssociations: [...(data !== null && data !== void 0 && data.contactId ? [{
        associationTypeId: 1043,
        associationCategory: 'HUBSPOT_DEFINED',
        toObjectTypeId: CONTACT_TYPE_ID,
        toObjectId: data.contactId.toString()
      }] : []), ...(data !== null && data !== void 0 && data.companyId ? [{
        associationTypeId: 1045,
        associationCategory: 'HUBSPOT_DEFINED',
        toObjectTypeId: COMPANY_TYPE_ID,
        toObjectId: data.companyId.toString()
      }] : []), ...(data !== null && data !== void 0 && data.lineItems && data.lineItems.length > 0 ? data.lineItems.map(lineItem => ({
        associationTypeId: 1053,
        associationCategory: 'HUBSPOT_DEFINED',
        toObjectTypeId: LINE_ITEM_TYPE_ID,
        toObjectId: lineItem.node.id.toString()
      })) : [])]
    });
  }, [permissions, canEditAssociation, trackEvent, openAssociationPanel, data === null || data === void 0 ? void 0 : data.title, data === null || data === void 0 ? void 0 : data.currencyCode, data === null || data === void 0 ? void 0 : data.contactId, data === null || data === void 0 ? void 0 : data.companyId, data === null || data === void 0 ? void 0 : data.lineItems, defaultCurrency]);
  return /*#__PURE__*/_jsx(Dropdown, {
    buttonUse: "transparent",
    buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "commerceContractLib.association.buttonLabels.primaryButtonLabel"
    }),
    buttonStyles: {
      paddingInlineEnd: '0'
    },
    open: open,
    onOpenChange: e => {
      if (!e.target.value) {
        setOpen(false);
      } else {
        setOpen(true);
      }
    },
    children: /*#__PURE__*/_jsxs(List, {
      children: [hasPrivateBetaGate && fromObjectTypeId === DEAL_TYPE_ID && /*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(ConvertToContractTooltip, {
          objectId: fromObjectId,
          objectTypeId: fromObjectTypeId,
          canEditAssociation: canEditAssociation,
          renderContent: ({
            loading,
            disabled
          }) => {
            return /*#__PURE__*/_jsx(LoadingButton, {
              disabled: disabled || !permissions.canEditAssociation(canEditAssociation),
              loading: loading || defaultCurrencyLoading,
              onClick: openConversionPanel,
              use: "link",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "commerceContractLib.association.buttonLabels.convertDeal"
              })
            });
          }
        })
      }), /*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(Tooltip, {
          placement: "left",
          disabled: canEditAssociation,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "commerceContractLib.association.validationMessages.missingPermission.createAssociation"
          }),
          children: /*#__PURE__*/_jsx(Button, {
            "data-test-id": "commerce-contract-association-card:association-button",
            disabled: !canEditAssociation,
            onClick: () => {
              trackEvent('Click Contract "Add Association"');
              openAssociationPanel();
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "commerceContractLib.association.buttonLabels.add"
            })
          })
        })
      })]
    })
  });
}